import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  HelpCircle
} from 'lucide-react';


const SOFTWARE_CATALOG = [
  {
    id: 'pscad',
    name: 'PSCAD / EMTDC v5',
    category: 'power',
    desc: 'Electromagnetic transient simulation for renewable grid integration, HVDC systems, and converter control design.',
    plans: ['Pro Computer', 'Enterprise Node', 'AI Dedicated'],
    demoSupported: true,
    tags: ['EMT Transients', 'HVDC', 'Grid Studies', 'Wind & Solar', 'IEEE Feeders'],
    specs: '60fps Low-Latency VNC • Dual Xeon Cores • Instant Node Provisioning'
  },
  {
    id: 'matlab',
    name: 'MATLAB & Simulink 2024',
    category: 'simulation',
    categoryLabel: 'Numerical & Control Systems',
    badge: 'Industry Standard',
    desc: 'High-speed matrix computation, dynamic control modeling, HDL coder, and Simscape electrical system simulation.',
    plans: ['Pro Computer', 'GPU Professional', 'Enterprise Node', 'AI Dedicated'],
    recommendedPlan: 'Pro Computer',
    demoSupported: true,
    tags: ['Simulink', 'Simscape', 'Control Systems', 'Parallel Toolbox', 'Stateflow'],
    specs: 'Parallel Computing Toolbox • Shared A100 GPU • Pre-configured Toolboxes'
  },
  {
    id: 'vivado',
    name: 'Xilinx Vivado & Vitis',
    category: 'vlsi',
    categoryLabel: 'VLSI & FPGA Hardware',
    badge: 'Hardware Flashing Ready',
    desc: 'Complete RTL synthesis, place-and-route, and Web-JTAG direct hardware bitstream flashing to connected FPGA boards.',
    plans: ['GPU Professional', 'Enterprise Node'],
    recommendedPlan: 'GPU Professional',
    demoSupported: false,
    tags: ['Artix-7', 'Zynq-7000', 'Verilog / VHDL', 'Web JTAG', 'Logic Analyzer'],
    specs: 'Direct Web JTAG bridge • Multi-angle HD Cam • Integrated 16-ch Logic Analyzer'
  },
  {
    id: 'ansys',
    name: 'ANSYS (HFSS / Maxwell)',
    category: 'simulation',
    categoryLabel: 'Electromagnetics & Multiphysics',
    badge: 'GPU Accelerated',
    desc: '3D high-frequency electromagnetic field simulation, electric motor design, and thermal finite element analysis.',
    plans: ['GPU Professional', 'Enterprise Node'],
    recommendedPlan: 'GPU Professional',
    demoSupported: false,
    tags: ['HFSS', 'Maxwell 2D/3D', 'FEA Thermal', 'Motor CAD', 'Antenna Array'],
    specs: 'Dedicated RTX 4500 (24GB VRAM) • 64GB DDR5 RAM • 32 Virtual Cores'
  },
  {
    id: 'etap',
    name: 'ETAP 2024 Electrical Suite',
    category: 'power',
    desc: 'Comprehensive electrical power system analysis software for load flow, short circuit, protection coordination, and arc flash.',
    plans: ['Pro Computer', 'Enterprise Node'],
    demoSupported: false,
    tags: ['Load Flow', 'Arc Flash', 'Relay Coordination', 'Harmonic Analysis', 'Microgrid'],
    specs: 'Pre-configured license manager • High-speed NVMe • Isolated workspace'
  },
  {
    id: 'digsilent',
    name: 'DigSILENT PowerFactory',
    category: 'power',
    categoryLabel: 'Power Systems & EMT',
    badge: 'Grid Utility Tier',
    desc: 'Advanced power system analysis tool for transmission, distribution, and industrial networks with RMS and EMT dynamic engines.',
    plans: ['Enterprise Node'],
    recommendedPlan: 'Enterprise Node',
    demoSupported: false,
    tags: ['Grid Code Compliance', 'RMS Dynamic', 'Contingency Analysis', 'Modal Analysis'],
    specs: 'AMD EPYC Enterprise Cluster • 128GB ECC RAM • Private IP VPN'
  },
  {
    id: 'pytorch-cuda',
    name: 'PyTorch 2.x & CUDA 12 HPC',
    category: 'ai',
    desc: 'Deep learning frameworks with hardware-accelerated TensorRT, Jupyter Lab, distributed GPU training, and LLM fine-tuning.',
    plans: ['GPU Professional', 'AI Dedicated'],
    demoSupported: false,
    tags: ['CUDA 12.1', 'TensorFlow', 'TensorRT', 'Jupyter Lab', 'LLM Fine-tuning'],
    specs: 'Dedicated NVIDIA H100 (80GB HBM3) • 256GB ECC RAM • Full Root SSH'
  },
  {
    id: 'quartus',
    name: 'Intel Quartus Prime & ModelSim',
    category: 'vlsi',
    categoryLabel: 'VLSI & FPGA Hardware',
    badge: 'Digital Design',
    desc: 'FPGA design environment for Cyclone V, MAX 10 and Agilex FPGAs with native ModelSim RTL timing verification.',
    plans: ['GPU Professional', 'Enterprise Node'],
    recommendedPlan: 'GPU Professional',
    demoSupported: false,
    tags: ['Cyclone V', 'ModelSim', 'VHDL / SystemVerilog', 'Timing Analysis', 'Nios II'],
    specs: 'Fast parallel compilation • USB-Blaster hardware link • Remote test bench'
  },
  {
    id: 'stm32',
    name: 'STM32CubeIDE & Keil uVision',
    category: 'embedded',
    categoryLabel: 'Embedded Systems & MCU',
    badge: 'Microcontroller Lab',
    desc: 'ARM Cortex-M firmware development, debugging via ST-Link remote server, and peripheral logic capture streaming.',
    plans: ['Developer Workstation', 'Standard Computer', 'Pro Computer'],
    recommendedPlan: 'Developer Workstation',
    demoSupported: false,
    tags: ['ARM Cortex-M', 'ST-Link V3', 'C / C++', 'Live Debugging', 'RTOS'],
    specs: 'Remote ST-Link server • Live camera viewing test bench • Serial UART terminal'
  },
  {
    id: 'solidworks',
    name: 'SolidWorks 3D CAD',
    category: 'simulation',
    categoryLabel: 'Mechanical & 3D Modeling',
    badge: 'Workstation Graphics',
    desc: 'Parametric 3D solid modeling, mechanical assembly design, and motion simulation powered by dedicated workstation GPUs.',
    plans: ['GPU Professional', 'Enterprise Node'],
    recommendedPlan: 'GPU Professional',
    demoSupported: false,
    tags: ['3D CAD', 'Motion Simulation', 'Thermal FEA', 'Assemblies', 'Ray-trace Render'],
    specs: '4K 60fps GPU streaming • RTX 4500 Dedicated VRAM • Low-latency input'
  },
  {
    id: 'comsol',
    name: 'COMSOL Multiphysics',
    category: 'simulation',
    categoryLabel: 'Coupled Physics & CFD',
    badge: 'High Performance',
    desc: 'Cross-disciplinary physics simulation for electromagnetics, fluid dynamics, heat transfer, and piezoelectric sensors.',
    plans: ['Enterprise Node', 'GPU Professional'],
    recommendedPlan: 'Enterprise Node',
    demoSupported: false,
    tags: ['CFD', 'Heat Transfer', 'Piezoelectric', 'Acoustics', 'MEMS'],
    specs: 'Multi-threaded EPYC nodes • High-memory allocation • Custom mesh solvers'
  },
  {
    id: 'labview',
    name: 'LabVIEW & NI Tools',
    category: 'embedded',
    categoryLabel: 'Virtual Instrumentation & DAQ',
    badge: 'Measurement Suite',
    desc: 'System-design platform and visual programming environment for automated test, data acquisition, and instrument control.',
    plans: ['Standard Computer', 'Pro Computer', 'Enterprise Node'],
    recommendedPlan: 'Pro Computer',
    demoSupported: false,
    tags: ['Data Acquisition', 'Virtual DAQ', 'Signal Processing', 'Test Automation'],
    specs: 'Virtual signal generators • Oscilloscope streams • Automated test pipelines'
  }
];



const Home = ({ setActiveTab }) => {
  const [openFaq, setOpenFaq] = useState(0);
  const [softwareSearchQuery] = useState('');
  const [showForegroundClouds, setShowForegroundClouds] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowForegroundClouds(true), 500);
    return () => clearTimeout(t);
  }, []);

  const filteredSoftwareList = SOFTWARE_CATALOG.filter((tool) => {
    const query = softwareSearchQuery.toLowerCase().trim();
    if (!query) return true;

    const matchesName = tool.name.toLowerCase().includes(query);
    const matchesDesc = tool.desc.toLowerCase().includes(query);
    const matchesCategoryLabel = tool.categoryLabel.toLowerCase().includes(query);
    const matchesTags = tool.tags.some(tag => tag.toLowerCase().includes(query));
    const matchesPlans = tool.plans.some(plan => plan.toLowerCase().includes(query));

    return matchesName || matchesDesc || matchesCategoryLabel || matchesTags || matchesPlans;
  });

  return (
    <div className="home-container">

      {/* ═══════════════════════════════════════
          HERO SECTION — Atmospheric Sky & Cloud Computing Theme
          ═══════════════════════════════════════ */}
      <section 
        className="home-hero-section relative overflow-hidden min-h-[calc(100vh-70px)] sm:min-h-[calc(100dvh-74px)] flex items-center justify-center"
        style={{
          background: 'linear-gradient(180deg, #7dd3fc 0%, #bae6fd 25%, #e0f2fe 55%, #f0f9ff 80%, #ffffff 100%)'
        }}
      >
        {/* Seamless Multi-Layer Real-Time Animated Cloud Atmosphere (Behind Text) */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          {/* Layer 1: Base Primary Ultra-HD Cumulus Cloud Real-Time Horizontal Wind Flow */}
          <div className="absolute -inset-y-12 left-0 w-[200%] flex animate-realtime-clouds-slow opacity-85">
            <div 
              className="w-1/2 h-full bg-cover bg-center bg-no-repeat shrink-0"
              style={{ 
                backgroundImage: `url('/sky_hero_bg.webp')`,
                filter: 'saturate(1.15) contrast(1.05) brightness(1.02)'
              }}
            />
            <div 
              className="w-1/2 h-full bg-cover bg-center bg-no-repeat shrink-0 scale-x-[-1]"
              style={{ 
                backgroundImage: `url('/sky_hero_bg.webp')`,
                filter: 'saturate(1.15) contrast(1.05) brightness(1.02)'
              }}
            />
          </div>

          {/* Layer 2: High-Altitude Cirrus & Wispy Parallax Cloud Drift */}
          <div className="absolute -inset-y-12 left-0 w-[200%] flex animate-realtime-clouds-parallax opacity-25">
            <div 
              className="w-1/2 h-full bg-cover bg-center bg-no-repeat shrink-0"
              style={{ 
                backgroundImage: `url('/sky_clouds_layer.webp')`,
                filter: 'brightness(1.05) contrast(1.02)'
              }}
            />
            <div 
              className="w-1/2 h-full bg-cover bg-center bg-no-repeat shrink-0 scale-x-[-1]"
              style={{ 
                backgroundImage: `url('/sky_clouds_layer.webp')`,
                filter: 'brightness(1.05) contrast(1.02)'
              }}
            />
          </div>

          {/* Gentle Ambient Daylight Softener (Keeps clouds fluffy & sky subtle) */}
          <div className="absolute inset-0 bg-gradient-to-b from-sky-400/10 via-transparent to-white/60 pointer-events-none"></div>

          {/* Bottom Seamless Gradient Fade into Pure White Content */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none"></div>
        </div>

        {/* ─── 3D FOREGROUND CLOUD PASS-THROUGH (WEAVING RIGHT-TO-LEFT IN FRONT OF AND ACROSS THE TEXT) ─── */}
        {showForegroundClouds && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-20 animate-fade-in">
            {/* 3D Cloud Plume 1: Glides Right-to-Left across "Next-Gen Remote Computing for Engineers" */}
            <div className="absolute top-[8%] sm:top-[12%] left-0 w-[600px] sm:w-[900px] h-[260px] sm:h-[360px] animate-cloud-3d-rtl-1 opacity-75 pointer-events-none">
              <div 
                className="w-full h-full bg-contain bg-center bg-no-repeat animate-cloud-3d-weave"
                style={{ 
                  backgroundImage: `url('/cloud_3d_stream.webp')`
                }}
              />
            </div>

            {/* 3D Cloud Plume 2: Glides Right-to-Left across the subtitle paragraph */}
            <div className="absolute top-[38%] sm:top-[42%] left-0 w-[550px] sm:w-[850px] h-[260px] sm:h-[360px] animate-cloud-3d-rtl-2 opacity-70 pointer-events-none">
              <div 
                className="w-full h-full bg-contain bg-center bg-no-repeat animate-cloud-3d-weave"
                style={{ 
                  backgroundImage: `url('/cloud_3d_puff.webp')`
                }}
              />
            </div>

            {/* 3D Cloud Plume 3: Low-altitude wispy vapor drifting Right-to-Left across the button area */}
            <div className="absolute top-[65%] sm:top-[68%] left-0 w-[650px] sm:w-[950px] h-[240px] sm:h-[320px] animate-cloud-3d-rtl-3 opacity-60 pointer-events-none">
              <div 
                className="w-full h-full bg-contain bg-center bg-no-repeat animate-cloud-3d-weave"
                style={{ 
                  backgroundImage: `url('/cloud_3d_stream.webp')`
                }}
              />
            </div>
          </div>
        )}

        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 flex flex-col items-center justify-center text-center relative z-10">
          <div className="space-y-6 sm:space-y-8 text-center max-w-4xl mx-auto flex flex-col items-center justify-center drop-shadow-sm">
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-semibold text-slate-900 tracking-tight leading-[1.2] pb-1 drop-shadow-[0_2px_14px_rgba(255,255,255,0.75)]">
              <span className="inline-block hover-float-text mr-[0.25em]">
                Next-Gen
              </span>
              <span className="inline-block mr-[0.25em]">
                <span className="text-gradient-green hero-animated-text hover:scale-[1.03] transition-transform duration-300 inline-block">
                  Remote Computing
                </span>
              </span>
              <span className="inline-block hover-float-text mr-[0.25em]">
                for
              </span>
              <span className="inline-block hover-float-text">
                Engineers
              </span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-slate-800 max-w-2xl mx-auto leading-relaxed font-normal drop-shadow-[0_1px_8px_rgba(255,255,255,0.85)]">
              Accelerate your engineering workflows with cloud-based access to PSCAD, MATLAB, FPGA hardware boards, and AI simulation environments. High performance, zero hardware overhead.
            </p>

            <div className="flex justify-center pt-3 sm:pt-4">
              <button
                onClick={() => setActiveTab('specs')}
                className="fb-btn-blue text-base sm:text-lg px-8 py-4 !rounded-none no-border-radius group shadow-lg hover:shadow-xl transition-all"
                style={{ borderRadius: '0px' }}
              >
                Explore Remote Lab Specs <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── REMOTE LAB CORE CAPABILITIES (FLAGSHIP PLATFORM - ZIGZAG GRID) ─── */}
      <section id="core-capabilities" style={{ backgroundColor: '#FAF8F5' }} className="py-12 sm:py-16 scroll-mt-24 border-y border-[#EAE4DA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#0f316e] tracking-tight sm:whitespace-nowrap">
              What Makes MP-X Remote Lab Superior?
            </h2>
          </div>

          <div className="border border-[#DFD7C9] divide-y divide-[#DFD7C9] shadow-sm overflow-hidden bg-white">
            {[
              {
                title: 'MP-X launches ultra-low latency cloud GUI for PSCAD, MATLAB, and CAD tools',
                image: '/premium_lab_workspace_home.webp',
                actionTab: 'specs',
                imageLeft: true
              },
              {
                title: 'Direct bitstream deployment and live multi-angle FPGA & MCU board verification',
                image: '/hardware_lab.webp',
                actionTab: 'lab',
                imageLeft: false
              },
              {
                title: 'Next-generation Dual Intel Xeon & NVIDIA RTX dedicated compute infrastructure',
                image: '/tech_server_node.webp',
                actionTab: 'specs',
                imageLeft: true
              }
            ].map((item, idx) => (
              <div
                key={idx}
                onClick={() => setActiveTab(item.actionTab)}
                className="grid grid-cols-1 md:grid-cols-2 items-stretch bg-white overflow-hidden group cursor-pointer transition-all duration-300"
              >
                {/* Image Section */}
                <div className={`relative h-56 sm:h-64 md:h-[290px] overflow-hidden ${item.imageLeft ? 'order-1 md:order-1' : 'order-1 md:order-2'}`}>
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none"></div>
                </div>

                {/* Content Section */}
                <div className={`p-6 sm:p-8 md:p-10 flex flex-col justify-between bg-white group-hover:bg-[#0cb5d2] transition-colors duration-300 ${item.imageLeft ? 'order-2 md:order-2' : 'order-2 md:order-1'}`}>
                  <div>
                    <h3 className="text-xl sm:text-2xl md:text-[26px] font-bold text-[#0f316e] group-hover:text-white leading-snug tracking-tight transition-colors duration-300">
                      {item.title}
                    </h3>
                  </div>

                  <div className="pt-6 flex justify-end">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(item.actionTab);
                      }}
                      className="w-10 h-10 bg-black group-hover:bg-white text-white group-hover:text-[#0cb5d2] flex items-center justify-center transition-all duration-300 cursor-pointer shadow-sm group-hover:scale-105"
                      title="Learn More"
                    >
                      <ChevronRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON: TRADITIONAL LAB VS MP-X REMOTE LAB ─── */}
      <section className="py-16 sm:py-20 relative overflow-hidden">
        {/* Bottom Half Cyan Background connecting to Target Domains below */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1/2 pointer-events-none mpx-cyan-split-bg" 
          style={{ backgroundColor: '#0cb5d2' }}
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f316e]">
              Why Switch to MP-X Remote Lab?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* Card 1: Traditional Physical Lab */}
            <div 
              style={{ backgroundColor: '#FAF8F5', borderColor: '#EAE4DA' }}
              className="border rounded-2xl p-6 sm:p-8 flex flex-col justify-between shadow-xl"
            >
              <div>
                <div className="pb-6 border-b border-[#EAE4DA]">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#0f316e]">
                    Traditional Physical Lab
                  </h3>
                </div>

                <div className="divide-y divide-[#EAE4DA] pt-2">
                  {[
                    { label: 'Initial Capital Cost', value: '$50,000+ CapEx per workstation' },
                    { label: 'Maintenance & Upgrades', value: 'Manual part replacement & depreciation' },
                    { label: 'Access & Availability', value: 'Restricted to office hours & physical desks' },
                    { label: 'Setup Lead Time', value: '3 to 6 weeks hardware procurement' },
                    { label: 'User Scaling', value: 'Hard bottlenecked by physical space' },
                    { label: 'Software Toolchains', value: 'Local license locks & manual patching' }
                  ].map((item, idx) => (
                    <div key={idx} className="py-3.5 flex justify-between items-start gap-4">
                      <span className="text-xs font-semibold text-gray-900">{item.label}</span>
                      <span className="text-xs text-gray-900 text-right font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Card 2: MP-X Remote Lab */}
            <div className="bg-gradient-to-br from-[#0e306d] to-[#082252] text-white rounded-2xl p-6 sm:p-8 flex flex-col justify-between shadow-xl relative overflow-hidden">
              {/* Subtle Ambient Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none"></div>

              <div className="relative z-10">
                <div className="pb-6 border-b border-white/10">
                  <h3 className="text-xl sm:text-2xl font-bold text-white">
                    MP-X Remote Lab
                  </h3>
                </div>

                <div className="divide-y divide-white/10 pt-2">
                  {[
                    { label: 'Initial Capital Cost', value: 'Zero CapEx (Flexible Pay-as-you-go)' },
                    { label: 'Maintenance & Upgrades', value: 'Fully Managed & Automatically Upgraded' },
                    { label: 'Access & Availability', value: '24/7 Global Access on Any Browser' },
                    { label: 'Setup Lead Time', value: 'Instant Node Provisioning (< 12h)' },
                    { label: 'User Scaling', value: 'Elastic Parallel Multi-User Cloud Nodes' },
                    { label: 'Software Toolchains', value: 'Pre-configured, Multi-tool Ready' }
                  ].map((item, idx) => (
                    <div key={idx} className="py-3.5 flex justify-between items-start gap-4">
                      <span className="text-xs font-semibold text-white">{item.label}</span>
                      <span className="text-xs font-bold text-white text-right">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── INDUSTRIES WE SERVE ─── */}
      <section className="py-16 sm:py-20 text-white relative overflow-hidden mpx-target-domains-section bg-[#0cb5d2]" style={{ backgroundColor: '#0cb5d2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-sm">
              Industries Empowered by MP-X
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Power Systems & Smart Grids',
                desc: 'EMT transient analysis, microgrid simulation using PSCAD and ETAP.',
                image: '/industry_power_grid.webp'
              },
              {
                title: 'VLSI & Semiconductor',
                desc: 'Remote FPGA synthesis, RTL debugging, and hardware board verification.',
                image: '/industry_semiconductor.webp'
              },
              {
                title: 'AI & High Performance Compute',
                desc: 'Accelerated neural network training and parallel CUDA algorithm execution.',
                image: '/industry_ai_compute.webp'
              },
              {
                title: 'Universities & EdTech',
                desc: 'Virtual hardware labs enabling remote learning for engineering students worldwide.',
                image: '/industry_edtech_lab.webp'
              }
            ].map((ind, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden border border-white/25 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col justify-end min-h-[320px] p-6 text-white"
              >
                {/* Lazy-loaded background image */}
                <img 
                  src={ind.image}
                  alt={ind.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Multi-Stop Dark Gradient Overlay for Maximum Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/65 to-slate-900/25 group-hover:from-slate-950/98 group-hover:via-slate-950/75 transition-colors" />

                {/* Card Content */}
                <div className="relative z-10 space-y-2">
                  <h4 className="text-lg font-bold text-white leading-snug tracking-tight">
                    {ind.title}
                  </h4>
                  <p className="text-xs text-gray-300 leading-relaxed font-normal">
                    {ind.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── INTERACTIVE SOFTWARE & HARDWARE TOOL QUICK-SEARCH ─── */}
      <section className="w-full py-12 sm:py-16 lg:py-20 border-y border-[#EAE4DA]" style={{ backgroundColor: '#FAF8F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-responsive-title font-black text-[#0f316e] tracking-tight">
              Find Your Software & Hardware Tool
            </h2>
          </div>

          {/* Filtered Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSoftwareList.map((tool) => (
              <div
                key={tool.id}
                className="bg-white p-5 sm:p-6 border border-[#EAE4DA] shadow-xs hover:shadow-md hover:border-[#3b82f6] transition-all duration-300 flex items-center justify-center text-center group cursor-default"
              >
                <h3 className="text-base sm:text-lg font-black text-[#0f316e] group-hover:text-[#3b82f6] transition-colors">
                  {tool.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ─── HOME PAGE FAQ ACCORDION ─── */}
      <section style={{ backgroundColor: '#FAF8F5' }} className="py-16 sm:py-20 border-b border-[#EAE4DA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-[#0f316e]">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 mt-2 text-sm">
              Everything you need to know about MP-X Remote Lab & computing services.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What is MP-X Remote Lab and how does it work?",
                a: "MP-X Remote Lab provides browser-based, high-performance remote access to engineering software (PSCAD, MATLAB, ETAP) and physical hardware development boards (FPGAs, Microcontrollers) without requiring local installation or expensive hardware."
              },
              {
                q: "Do I need to install any software on my computer?",
                a: "No! All simulation tools run inside secure cloud servers. You only need a standard web browser (Chrome, Firefox, Edge) to connect to the HD remote desktop."
              },
              {
                q: "Can I flash code to actual hardware development boards remotely?",
                a: "Yes, MP-X supports hardware-in-the-loop testing. You can remotely upload bitstreams to connected FPGA kits and monitor pin outputs via live HD video streaming."
              },
              {
                q: "Is my proprietary project code and simulation data secure?",
                a: "Absolutely. Every user session runs in an isolated VLAN sandbox with enterprise AES-256 encryption. We offer zero data retention options for sensitive defense and enterprise projects."
              },
              {
                q: "How fast can I get my team setup with Remote Lab access?",
                a: "Standard accounts and instant demo sandboxes are available immediately. Customized multi-node enterprise environments can be provisioned in under 12 hours."
              }
            ].map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div key={index} className="bg-white rounded-2xl border border-[#EAE4DA] overflow-hidden shadow-xs">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    className="w-full p-5 text-left flex justify-between items-center gap-4 font-bold text-gray-900 text-sm sm:text-base hover:text-blue-600 transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <HelpCircle size={18} className="text-blue-500 shrink-0" />
                      {faq.q}
                    </span>
                    {isOpen ? <ChevronUp size={18} className="text-blue-600 shrink-0" /> : <ChevronDown size={18} className="text-gray-400 shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-[#EAE4DA] mt-1">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {/* ─── CTA SECTION ─── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div
          style={{ backgroundColor: '#FAF8F5', borderColor: '#EAE4DA' }}
          className="relative text-center mx-auto text-gray-900 border rounded-3xl p-8 sm:p-12 shadow-sm"
        >
          <div className="max-w-xl space-y-4 relative z-10 mx-auto">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0f316e] tracking-tight leading-tight">
              Ready to scale your capacity?
            </h2>
            <p className="text-sm sm:text-base text-gray-500 leading-relaxed max-w-md mx-auto">
              Join leading engineering firms already using MP-X Global Solutions.
            </p>
            <div className="flex justify-center pt-1">
              <button
                onClick={() => setActiveTab('contact', 'reach-out-form')}
                className="fb-btn-blue text-sm sm:text-base py-3 px-8 !rounded-none"
                style={{ borderRadius: 0 }}
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
